import React, {useState, useEffect, Fragment} from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Tabs, Tab, Box, Table, TableHead, TableRow, TableCell, TableBody, Button,
  Typography, TextField, Select, MenuItem, Chip, Checkbox, FormControl, FormControlLabel, IconButton, InputLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { useSelector, useDispatch } from 'react-redux';
/** Components */
import Skeleton from '@mui/material/Skeleton';
import PageContainer from '../../components/PageContainer';
import { AppDialog, EmptyState, PageHeader, Panel, StatusChip } from '../../components/ui';
import TagPickerPopup, { TagPickerPanel } from '../../components/TagPickerPopup';
/** Selectors */
import {IStore} from "../../store/types";
import {StoryTags} from '../../store/slices'
import {useSearchParams} from "react-router-dom";
import {createStoryTagThunk} from "../../store/thunk";

const DEFAULT_PAGING_STATE = {
  perPage: 200,
  page: 1,
  total: 0
};


const DEFAULT_TAG_GROUPS_PAGING = {
  perPage: 25,
  page: 1,
  total: 0,
};

const StoryTagsPage = () => {
  const theme = useTheme();
  const {
    storyTags,
    status,
    queryParams,
    tagGroupsStatus,
    tagGroupsQueryParams,
  } = useSelector<IStore, { storyTags: any, status: string , queryParams: any, tagGroupsStatus: string, tagGroupsQueryParams: any}>(state => ({
    storyTags: state.storyTags.data,
    status: state.storyTags.status,
    queryParams: state.storyTags.queryParams,
    tagGroupsStatus: state.storyTags.tagGroupsStatus,
    tagGroupsQueryParams: state.storyTags.tagGroupsQueryParams,
  }))

  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const authorId = searchParams.get("authorId");

  const loadStoriesTags = (params) => {
    dispatch<any>(StoryTags.getStoryTags(params));
  };

  const loadTagGroups = (params?: { page?: number; perPage?: number; isDeleted?: 0 | 1 }) => {
    dispatch<any>(StoryTags.getTagGroups({
      page: params?.page ?? tagGroupsQueryParams?.page ?? DEFAULT_TAG_GROUPS_PAGING.page,
      perPage: params?.perPage ?? tagGroupsQueryParams?.perPage ?? DEFAULT_TAG_GROUPS_PAGING.perPage,
      isDeleted: params?.isDeleted ?? 0,
    }));
  };

  useEffect(() => {
      loadStoriesTags({
      ...queryParams,
      page: queryParams?.page || DEFAULT_PAGING_STATE.page,
      perPage: queryParams?.perPage || DEFAULT_PAGING_STATE.perPage,
      authorId
    });
    // Mount / author change only, as before; edits reload explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorId])

  useEffect(() => {
    if (storyTags?.tags) {
      const tagMap: Record<number, string> = {};
      storyTags.tags.forEach((tag: any) => {
        tagMap[tag.id] = tag.name;
      });
      setTagNames(tagMap);
    }

    if (storyTags?.categories) {
      const catMap: Record<number, string> = {};
      storyTags.categories.forEach((cat: any) => {
        catMap[cat.id] = cat.name;
      });
      setCategoryNames(catMap);
    }
  }, [storyTags?.tags, storyTags?.categories]);
  const [tab, setTab] = useState(0);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };
  // --- Додаємо стан для табів категорій ---
  const [tagCategoryTab, setTagCategoryTab] = useState(0);

  const sensors = useSensors(
    useSensor(PointerSensor)
  );
  // --- Додаємо стан для порядку категорій ---
  const [orderedCategories, setOrderedCategories] = useState<any[]>([]);
  useEffect(() => {
    setTagCategoryTab(0);
    if (storyTags?.categories) {
      const filtered = storyTags.categories;
      const sorted = [...filtered].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setOrderedCategories(sorted);
    }
  }, [storyTags.categories]);

// --- SortableRow component for dnd-kit ---
const SortableRow = ({ category, index, children, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: category.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: disabled ? theme.custom.color.surfaceMuted : 'inherit',
    opacity: disabled ? 0.6 : 1,
    pointerEvents: disabled ? ('none' as React.CSSProperties['pointerEvents']) : ('auto' as React.CSSProperties['pointerEvents']),
  };

  return (
    <TableRow ref={setNodeRef} style={style}>
      <TableCell sx={{ width: 40 }}>
        <span
          {...(!disabled ? attributes : {})}
          {...(!disabled ? listeners : {})}
          style={{ cursor: 'grab', userSelect: 'none', display: 'flex', alignItems: 'center' }}
        >
          <DragIndicatorIcon fontSize="small" />
        </span>
      </TableCell>
      {children}
    </TableRow>
  );
};

  const [tagNames, setTagNames] = useState<Record<number, string>>({});
  const [changedTags, setChangedTags] = useState<Set<number>>(new Set());
  const [categoryNames, setCategoryNames] = useState<Record<number, string>>({});
  const [changedCategories, setChangedCategories] = useState<Set<number>>(new Set());

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingCategoryId, setPendingCategoryId] = useState<number | null>(null);

  // --- Додаємо стани для оновлення категорій ---
  const [updatingCategoryIds, setUpdatingCategoryIds] = useState<Set<number>>(new Set());
  const [updatingTagIds, setUpdatingTagIds] = useState<Set<number>>(new Set());

  const [createCategoryOpen, setCreateCategoryOpen] = useState(false);
  const [createTagOpen, setCreateTagOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagCategoryId, setNewTagCategoryId] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);

  // Tag Groups state
  const [showDeletedTagGroups, setShowDeletedTagGroups] = useState(false);
  const [createTagGroupOpen, setCreateTagGroupOpen] = useState(false);
  const [newTagGroupName, setNewTagGroupName] = useState('');
  const [newTagGroupTagIds, setNewTagGroupTagIds] = useState<number[]>([]);
  const [creatingTagGroup, setCreatingTagGroup] = useState(false);
  const [updatingTagGroupIds, setUpdatingTagGroupIds] = useState<Set<number>>(new Set());
  const [tagGroupNames, setTagGroupNames] = useState<Record<number, string>>({});
  const [tagGroupTagIds, setTagGroupTagIds] = useState<Record<number, number[]>>({});
  const [changedTagGroups, setChangedTagGroups] = useState<Set<number>>(new Set());
  const [tagPickerOpen, setTagPickerOpen] = useState(false);
  const [tagPickerGroupId, setTagPickerGroupId] = useState<number | null>(null);
  const [savingTagPicker, setSavingTagPicker] = useState(false);

  useEffect(() => {
    loadTagGroups({ isDeleted: showDeletedTagGroups ? 1 : 0 });
    // Reload when the "show deleted" switch changes; the loader reads paging from the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeletedTagGroups]);

  useEffect(() => {
    if (storyTags?.tagGroups) {
      const names: Record<number, string> = {};
      const tagIdsMap: Record<number, number[]> = {};
      storyTags.tagGroups.forEach((g: any) => {
        names[g.id] = g.name;
        tagIdsMap[g.id] = g.tagIds || [];
      });
      setTagGroupNames(names);
      setTagGroupTagIds(tagIdsMap);
      setChangedTagGroups(new Set());
    }
  }, [storyTags?.tagGroups]);

  const getTagNameById = (tagId: number) => {
    const tag = (storyTags?.tags || []).find((t: any) => t.id === tagId);
    return tag?.name || `#${tagId}`;
  };

  const openTagPicker = (groupId: number) => {
    setTagPickerGroupId(groupId);
    setTagPickerOpen(true);
  };

  const closeTagPicker = () => {
    if (savingTagPicker) return;
    setTagPickerOpen(false);
    setTagPickerGroupId(null);
  };

  const handleTagPickerSave = async (ids: number[]) => {
    if (tagPickerGroupId === null) return;
    const group = (storyTags?.tagGroups || []).find((g: any) => g.id === tagPickerGroupId);
    if (!group) return;

    setSavingTagPicker(true);
    setUpdatingTagGroupIds((prev) => new Set(prev).add(tagPickerGroupId));
    try {
      await dispatch<any>(StoryTags.updateTagGroupThunk({
        id: tagPickerGroupId,
        formData: {
          name: tagGroupNames[tagPickerGroupId] ?? group.name,
          isDeleted: group.isDeleted ? 1 : 0,
          tagIds: ids,
        },
        onSuccess: () => {
          setTagGroupTagIds((prev) => ({ ...prev, [tagPickerGroupId]: ids }));
          markTagGroupChanged(
            tagPickerGroupId,
            tagGroupNames[tagPickerGroupId] ?? group.name,
            ids
          );
          setTagPickerOpen(false);
          setTagPickerGroupId(null);
        },
      }));
    } catch (e) {
      console.error('Failed to save tag group tags', e);
    } finally {
      setSavingTagPicker(false);
      setUpdatingTagGroupIds((prev) => {
        const copy = new Set(prev);
        copy.delete(tagPickerGroupId);
        return copy;
      });
    }
  };

  const markTagGroupChanged = (groupId: number, name: string, tagIds: number[]) => {
    const original = (storyTags?.tagGroups || []).find((g: any) => g.id === groupId);
    if (!original) return;
    const originalTagIds = original.tagIds || [];
    const tagsEqual =
      tagIds.length === originalTagIds.length &&
      tagIds.every((id) => originalTagIds.includes(id));
    const changed = name !== original.name || !tagsEqual;
    setChangedTagGroups((prev) => {
      const copy = new Set(prev);
      if (changed) copy.add(groupId);
      else copy.delete(groupId);
      return copy;
    });
  };

  const handleSaveTagGroup = async (groupId: number) => {
    const original = (storyTags?.tagGroups || []).find((g: any) => g.id === groupId);
    if (!original) return;
    setUpdatingTagGroupIds((prev) => new Set(prev).add(groupId));
    try {
      await dispatch<any>(StoryTags.updateTagGroupThunk({
        id: groupId,
        formData: {
          name: tagGroupNames[groupId] ?? original.name,
          isDeleted: original.isDeleted ? 1 : 0,
          tagIds: tagGroupTagIds[groupId] ?? original.tagIds ?? [],
        },
        onSuccess: () => {
          setChangedTagGroups((prev) => {
            const copy = new Set(prev);
            copy.delete(groupId);
            return copy;
          });
        },
      }));
    } catch (e) {
      console.error('Failed to update tag group', e);
    } finally {
      setUpdatingTagGroupIds((prev) => {
        const copy = new Set(prev);
        copy.delete(groupId);
        return copy;
      });
    }
  };

  const handleToggleTagGroupDelete = async (groupId: number) => {
    const group = (storyTags?.tagGroups || []).find((g: any) => g.id === groupId);
    if (!group) return;
    setUpdatingTagGroupIds((prev) => new Set(prev).add(groupId));
    try {
      await dispatch<any>(StoryTags.updateTagGroupThunk({
        id: groupId,
        formData: {
          name: group.name,
          isDeleted: group.isDeleted ? 0 : 1,
          tagIds: group.tagIds || [],
        },
        onSuccess: () => {
          loadTagGroups({ isDeleted: showDeletedTagGroups ? 1 : 0 });
        },
      }));
    } catch (e) {
      console.error('Failed to toggle tag group', e);
    } finally {
      setUpdatingTagGroupIds((prev) => {
        const copy = new Set(prev);
        copy.delete(groupId);
        return copy;
      });
    }
  };

  const handleSaveTag = async (tagId: number) => {
    const newName = tagNames[tagId];
    const original = storyTags.tags.find((t: any) => t.id === tagId);
    if (!original) return;

    setUpdatingTagIds(prev => new Set(prev).add(tagId));
    try {
      await dispatch<any>(StoryTags.updateStoryTagThunk({
        id: tagId,
        formData: {
          name: newName,
          categoryId: original.categoryId,
          isDeleted: original.isDeleted ? 1 : 0
        },
        onSuccess: () => {
          setChangedTags(prev => {
            const copy = new Set(prev);
            copy.delete(tagId);
            return copy;
          });
        }
      }));
    } catch (e) {
      console.error('Failed to update tag', e);
    } finally {
      setUpdatingTagIds(prev => {
        const copy = new Set(prev);
        copy.delete(tagId);
        return copy;
      });
    }
  };

  const handleSaveCategory = async (categoryId: number) => {
    const newName = categoryNames[categoryId];
    const original = storyTags.categories.find((c: any) => c.id === categoryId);
    if (!original) return;

    setUpdatingCategoryIds(prev => new Set(prev).add(categoryId));
    try {
      await dispatch<any>(StoryTags.updateStoryTagCategoryThunk({
        id: categoryId,
        formData: {
          name: newName,
          isDeleted: original.isDeleted ? 1 : 0
        },
        onSuccess: () => {
          setChangedCategories(prev => {
            const copy = new Set(prev);
            copy.delete(categoryId);
            return copy;
          });
        }
      }));
    } catch (e) {
      console.error('Failed to update category', e);
    } finally {
      setUpdatingCategoryIds(prev => {
        const copy = new Set(prev);
        copy.delete(categoryId);
        return copy;
      });
    }
  };

  // Toggle delete for tag
  const handleToggleTagDelete = async (tagId: number) => {
    const tag = storyTags.tags.find((t: any) => t.id === tagId);
    if (!tag) return;

    setUpdatingTagIds(prev => new Set(prev).add(tagId));
    try {
      await dispatch<any>(StoryTags.updateStoryTagThunk({
        id: tagId,
        formData: {
          name: tag.name,
          categoryId: tag.categoryId,
          isDeleted: tag.isDeleted ? 0 : 1
        }
      }));
    } catch (e) {
      console.error('Failed to toggle tag', e);
    } finally {
      setUpdatingTagIds(prev => {
        const copy = new Set(prev);
        copy.delete(tagId);
        return copy;
      });
    }
  };

  // Toggle delete for category (opens confirmation dialog)
  const handleToggleCategoryDelete = (categoryId: number) => {
    setPendingCategoryId(categoryId);
    setConfirmDialogOpen(true);
  };

  const confirmCategoryDeletion = async () => {
    if (pendingCategoryId === null) return;

    const category = storyTags.categories.find((c: any) => c.id === pendingCategoryId);
    if (!category) return;

    setUpdatingCategoryIds(prev => new Set(prev).add(pendingCategoryId));
    try {
      await dispatch<any>(StoryTags.updateStoryTagCategoryThunk({
        id: pendingCategoryId,
        formData: {
          name: category.name,
          isDeleted: category.isDeleted ? 0 : 1
        },
        onSuccess: () => {
          setConfirmDialogOpen(false);
          setPendingCategoryId(null);
          loadStoriesTags({...queryParams});
          setTagCategoryTab(0);
        }
      }));
    } catch (e) {
      console.error('Failed to toggle category', e);
    } finally {
      setUpdatingCategoryIds(prev => {
        const copy = new Set(prev);
        copy.delete(pendingCategoryId);
        return copy;
      });
    }
  };

  const pendingCategory = pendingCategoryId !== null ? storyTags.categories?.find((c: any) => c.id === pendingCategoryId) : null;
  const restoringCategory = Boolean(pendingCategory?.isDeleted);
  const initialLoading = status === 'loading' && (!storyTags || (storyTags.tags?.length === 0 && (!storyTags.categories || storyTags.categories.length === 0)));

  const rowActions = (changed: boolean, updating: boolean, deleted: boolean, onSave: () => void, onToggle: () => void) => (
    <Box display="flex" justifyContent="flex-end" gap={1}>
      <LoadingButton variant="contained" size="small" sx={{ minWidth: 72 }} disabled={!changed || updating} loading={updating} onClick={onSave}>
        Save
      </LoadingButton>
      <LoadingButton variant="outlined" color={deleted ? 'success' : 'error'} size="small" loading={updating} onClick={onToggle}>
        {deleted ? 'Restore' : 'Delete'}
      </LoadingButton>
    </Box>
  );

  // Tags grouped by (non-deleted) category, in category order.
  const categoryIdToNameMap = Object.fromEntries((storyTags?.categories || []).map((c: any) => [c.id, c.name]));
  const grouped: Record<string, any[]> = {};
  (storyTags?.categories || []).forEach((cat: any) => {
    if (!cat.isDeleted) grouped[cat.name] = [];
  });
  (storyTags?.tags || []).forEach((tag: any) => {
    const categoryName = categoryIdToNameMap[tag.categoryId];
    if (categoryName && grouped[categoryName]) grouped[categoryName].push(tag);
  });
  const sortedGroups = Object.entries(grouped).sort((a, b) => {
    const aCategory = (storyTags.categories || []).find((c: any) => c.name === a[0]);
    const bCategory = (storyTags.categories || []).find((c: any) => c.name === b[0]);
    return (aCategory?.order ?? 0) - (bCategory?.order ?? 0);
  });
  const [currentCategory, currentTagsRaw] = sortedGroups[tagCategoryTab] || ['-', []];
  const currentTags = currentTagsRaw as any[];

  return (
    <>
      <PageContainer constrained>
        <PageHeader
          eyebrow="Customer Stories"
          title="Tags and categories"
          subtitle="The taxonomy behind story tags: categories, their tags and reusable tag groups. Deleting hides; nothing is removed for good."
          actions={(
            <>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setCreateCategoryOpen(true)}>New category</Button>
              <Button variant={tab === 2 ? 'outlined' : 'contained'} startIcon={<AddIcon />} onClick={() => setCreateTagOpen(true)}>New tag</Button>
              {tab === 2 && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setNewTagGroupName('');
                    setNewTagGroupTagIds([]);
                    setCreateTagGroupOpen(true);
                  }}
                >
                  New tag group
                </Button>
              )}
            </>
          )}
        >
          <Tabs value={tab} onChange={handleTabChange} aria-label="Tags, categories and groups">
            <Tab label="Tags by category" />
            <Tab label="Categories" />
            <Tab label="Tag groups" />
          </Tabs>
        </PageHeader>

        {initialLoading && <Skeleton animation="wave" width="100%" height={420} variant="rounded" />}

        {!initialLoading && tab === 0 && (
          <Panel padding="none" subtitle={status === 'loading' ? 'Refreshing…' : undefined}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1, bgcolor: theme.custom.color.surfaceMuted }}>
              <Tabs value={Math.min(tagCategoryTab, Math.max(0, sortedGroups.length - 1))} onChange={(_e, newVal) => setTagCategoryTab(newVal)} variant="scrollable" scrollButtons="auto" aria-label="Categories">
                {sortedGroups.map(([category, list]) => (
                  <Tab label={`${category} (${(list as any[]).length})`} key={category} />
                ))}
              </Tabs>
            </Box>
            {currentTags.length === 0 ? (
              <EmptyState title={`No tags in ${currentCategory}`} hint="Create a tag and pick this category." action={<Button variant="outlined" size="small" onClick={() => setCreateTagOpen(true)}>New tag</Button>} />
            ) : (
              <Table aria-label={`Tags in ${currentCategory}`}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 90 }}>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell sx={{ width: 120 }}>Category</TableCell>
                    <TableCell sx={{ width: 220 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentTags.map((tag: any) => (
                    <TableRow key={tag.id} hover sx={{ opacity: tag.isDeleted ? 0.6 : 1 }}>
                      <TableCell sx={{ fontFamily: theme.custom.font.mono, fontSize: 13 }}>{tag.id}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={tagNames[tag.id] ?? tag.name}
                          inputProps={{ 'aria-label': `Name of tag ${tag.id}` }}
                          onChange={(e) => {
                            const newName = e.target.value;
                            setTagNames(prev => ({ ...prev, [tag.id]: newName }));
                            if (newName !== tag.name) {
                              setChangedTags(prev => new Set(prev).add(tag.id));
                            } else {
                              setChangedTags(prev => {
                                const copy = new Set(prev);
                                copy.delete(tag.id);
                                return copy;
                              });
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: theme.custom.font.mono, fontSize: 13 }}>{tag.categoryId}</TableCell>
                      <TableCell align="right">
                        {rowActions(changedTags.has(tag.id), updatingTagIds.has(tag.id), Boolean(tag.isDeleted), () => handleSaveTag(tag.id), () => handleToggleTagDelete(tag.id))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Panel>
        )}

        {!initialLoading && tab === 1 && (
          <Panel padding="none" title="Categories" subtitle="Drag the handle to change the order shown everywhere." badges={<StatusChip size="sm" tone="neutral">{orderedCategories.length}</StatusChip>}>
            <Table aria-label="Categories">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }} aria-label="Reorder" />
                  <TableCell sx={{ width: 90 }}>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right" sx={{ width: 220 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={({ active, over }) => {
                  if (active.id !== over?.id) {
                    const oldIndex = orderedCategories.findIndex(c => c.id === active.id);
                    const newIndex = orderedCategories.findIndex(c => c.id === over?.id);
                    const newOrder = arrayMove(orderedCategories, oldIndex, newIndex);
                    const updated = newOrder.map((cat, index) => ({ id: cat.id, order: index + 1 }));
                    const previousOrder = [...orderedCategories];
                    setOrderedCategories(newOrder);
                    dispatch<any>(StoryTags.updateCategoryOrderThunk({ categories: updated }))
                      .then(() => {})
                      .catch(() => {
                        setOrderedCategories(previousOrder);
                      });
                  }
                }}
              >
                <SortableContext items={orderedCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                  <TableBody>
                    {orderedCategories.map((category: any, index: number) => (
                      <SortableRow key={category.id} category={category} index={index} disabled={status === 'loading'}>
                        <TableCell sx={{ fontFamily: theme.custom.font.mono, fontSize: 13 }}>{category.id}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={categoryNames[category.id] ?? category.name}
                            inputProps={{ 'aria-label': `Name of category ${category.id}` }}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setCategoryNames(prev => ({ ...prev, [category.id]: newName }));
                              if (newName !== category.name) {
                                setChangedCategories(prev => new Set(prev).add(category.id));
                              } else {
                                setChangedCategories(prev => {
                                  const copy = new Set(prev);
                                  copy.delete(category.id);
                                  return copy;
                                });
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          {rowActions(changedCategories.has(category.id), updatingCategoryIds.has(category.id), Boolean(category.isDeleted), () => handleSaveCategory(category.id), () => handleToggleCategoryDelete(category.id))}
                        </TableCell>
                      </SortableRow>
                    ))}
                  </TableBody>
                </SortableContext>
              </DndContext>
            </Table>
          </Panel>
        )}

        {!initialLoading && tab === 2 && (
          <Panel
            padding="none"
            title="Tag groups"
            subtitle={tagGroupsStatus === 'loading' ? 'Loading…' : undefined}
            actions={(
              <FormControlLabel
                control={<Checkbox size="small" checked={showDeletedTagGroups} onChange={(e) => setShowDeletedTagGroups(e.target.checked)} />}
                label="Show deleted"
              />
            )}
          >
            {(storyTags?.tagGroups || []).length === 0 && tagGroupsStatus !== 'loading' ? (
              <EmptyState title="No tag groups" hint={showDeletedTagGroups ? 'There are no deleted groups.' : 'A group bundles tags that are often set together.'} />
            ) : (
              <Table aria-label="Tag groups">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 80 }}>ID</TableCell>
                    <TableCell sx={{ minWidth: 180 }}>Name</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell align="right" sx={{ width: 220 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(storyTags?.tagGroups || []).map((group: any) => {
                    const ids: number[] = tagGroupTagIds[group.id] ?? group.tagIds ?? [];
                    return (
                      <TableRow key={group.id} hover sx={{ opacity: group.isDeleted ? 0.6 : 1 }}>
                        <TableCell sx={{ fontFamily: theme.custom.font.mono, fontSize: 13 }}>{group.id}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={tagGroupNames[group.id] ?? group.name}
                            inputProps={{ maxLength: 128, 'aria-label': `Name of group ${group.id}` }}
                            onChange={(e) => {
                              const newName = e.target.value;
                              setTagGroupNames((prev) => ({ ...prev, [group.id]: newName }));
                              markTagGroupChanged(group.id, newName, tagGroupTagIds[group.id] ?? group.tagIds ?? []);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, flex: 1 }}>
                              {ids.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No tags</Typography>
                              ) : (
                                ids.slice(0, 8).map((id: number) => <Chip key={id} size="small" label={getTagNameById(id)} />)
                              )}
                              {ids.length > 8 && <Chip size="small" label={`+${ids.length - 8}`} />}
                            </Box>
                            <IconButton size="small" onClick={() => openTagPicker(group.id)} aria-label={`Edit tags of group ${group.id}`}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          {rowActions(changedTagGroups.has(group.id), updatingTagGroupIds.has(group.id), Boolean(group.isDeleted), () => handleSaveTagGroup(group.id), () => handleToggleTagGroupDelete(group.id))}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Panel>
        )}
      </PageContainer>

      <AppDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        title={restoringCategory ? `Restore “${pendingCategory?.name ?? ''}”?` : `Delete “${pendingCategory?.name ?? ''}”?`}
        description={restoringCategory
          ? 'Restoring this category also restores all of its tags and makes them visible again.'
          : 'Deleting this category also deletes all tags in it. They are hidden, not removed, and can be restored later.'}
        pending={pendingCategoryId !== null && updatingCategoryIds.has(pendingCategoryId)}
        maxWidth="xs"
        actions={(
          <>
            <Button variant="outlined" onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
            <Button color={restoringCategory ? 'primary' : 'error'} variant="contained" onClick={confirmCategoryDeletion}>
              {restoringCategory ? 'Restore' : 'Delete'}
            </Button>
          </>
        )}
      />

      <AppDialog
        open={createCategoryOpen}
        onClose={() => setCreateCategoryOpen(false)}
        title="New category"
        description="A category groups tags and becomes a column on the Stories tags page."
        pending={creatingCategory}
        maxWidth="xs"
        actions={(
          <>
            <Button variant="outlined" onClick={() => setCreateCategoryOpen(false)}>Cancel</Button>
            <LoadingButton
              variant="contained"
              onClick={async () => {
                setCreatingCategory(true);
                try {
                  await dispatch<any>(StoryTags.CreateStoryTagCategory({
                    formData: { name: newCategoryName },
                    onSuccess: () => {
                      setCreateCategoryOpen(false);
                      setNewCategoryName('');
                    }
                  }));
                } catch (e) {
                  console.error('Failed to create category', e);
                } finally {
                  loadStoriesTags({...queryParams});
                  setCreatingCategory(false);
                }
              }}
              disabled={creatingCategory || !newCategoryName.trim()}
              loading={creatingCategory}
            >
              Create category
            </LoadingButton>
          </>
        )}
      >
        <TextField
          autoFocus
          size="small"
          label="Category name"
          fullWidth
          value={newCategoryName}
          onChange={(e) => setNewCategoryName(e.target.value)}
          disabled={creatingCategory}
        />
      </AppDialog>

      <AppDialog
        open={createTagOpen}
        onClose={() => setCreateTagOpen(false)}
        title="New tag"
        pending={creatingTag}
        maxWidth="xs"
        actions={(
          <>
            <Button variant="outlined" onClick={() => setCreateTagOpen(false)}>Cancel</Button>
            <LoadingButton
              variant="contained"
              onClick={async () => {
                setCreatingTag(true);
                try {
                  await dispatch<any>(createStoryTagThunk({
                    formData: {
                      name: newTagName,
                      categoryId: Number(newTagCategoryId)
                    },
                    onSuccess: () => {
                      setCreateTagOpen(false);
                      setNewTagName('');
                      setNewTagCategoryId('');
                    }
                  }));
                } catch (e) {
                  console.error('Failed to create tag', e);
                } finally {
                  setCreatingTag(false);
                }
              }}
              disabled={creatingTag || !newTagName.trim() || !newTagCategoryId}
              loading={creatingTag}
            >
              Create tag
            </LoadingButton>
          </>
        )}
      >
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <TextField
            autoFocus
            size="small"
            label="Tag name"
            fullWidth
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            disabled={creatingTag}
          />
          <FormControl fullWidth size="small">
            <InputLabel id="new-tag-category-label">Category</InputLabel>
            <Select
              labelId="new-tag-category-label"
              label="Category"
              value={newTagCategoryId}
              onChange={(e) => setNewTagCategoryId(e.target.value)}
              disabled={creatingTag}
            >
              {storyTags?.categories?.filter((c: any) => !c.isDeleted).map((cat: any) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </AppDialog>

      <AppDialog
        open={createTagGroupOpen}
        onClose={() => {
          if (creatingTagGroup) return;
          setCreateTagGroupOpen(false);
        }}
        title="New tag group"
        description="Give the group a name and pick the tags it bundles."
        pending={creatingTagGroup}
        maxWidth="md"
        actions={(
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>Selected tags: {newTagGroupTagIds.length}</Typography>
            <Button variant="outlined" onClick={() => setCreateTagGroupOpen(false)} disabled={creatingTagGroup}>Cancel</Button>
            <LoadingButton
              variant="contained"
              disabled={creatingTagGroup || !newTagGroupName.trim() || newTagGroupTagIds.length === 0}
              loading={creatingTagGroup}
              onClick={async () => {
                setCreatingTagGroup(true);
                try {
                  await dispatch<any>(StoryTags.createTagGroupThunk({
                    formData: {
                      name: newTagGroupName.trim(),
                      tagIds: Array.from(new Set(newTagGroupTagIds)),
                    },
                    onSuccess: () => {
                      setCreateTagGroupOpen(false);
                      setNewTagGroupName('');
                      setNewTagGroupTagIds([]);
                      loadTagGroups({ isDeleted: showDeletedTagGroups ? 1 : 0 });
                    },
                  }));
                } catch (e) {
                  console.error('Failed to create tag group', e);
                } finally {
                  setCreatingTagGroup(false);
                }
              }}
            >
              Create group
            </LoadingButton>
          </>
        )}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '60vh' }}>
          <TextField
            autoFocus
            size="small"
            label="Group name"
            fullWidth
            value={newTagGroupName}
            onChange={(e) => setNewTagGroupName(e.target.value)}
            disabled={creatingTagGroup}
            inputProps={{ maxLength: 128 }}
            sx={{ mb: 2 }}
          />
          <TagPickerPanel
            key={createTagGroupOpen ? 'create-open' : 'create-closed'}
            tags={storyTags?.tags || []}
            categories={storyTags?.categories || []}
            selectedTagIds={newTagGroupTagIds}
            onChange={setNewTagGroupTagIds}
            disabled={creatingTagGroup}
          />
        </Box>
      </AppDialog>

      <TagPickerPopup
        open={tagPickerOpen}
        onClose={closeTagPicker}
        title={`Edit tags — ${tagPickerGroupId != null ? (tagGroupNames[tagPickerGroupId] || '') : ''}`}
        tags={storyTags?.tags || []}
        categories={storyTags?.categories || []}
        selectedTagIds={tagPickerGroupId != null ? (tagGroupTagIds[tagPickerGroupId] ?? []) : []}
        onConfirm={handleTagPickerSave}
        confirmLabel="Save"
        loading={savingTagPicker}
      />
    </>
  );
};

export default StoryTagsPage;
